import * as React from "react";
import {
  ScatterChart as RechartsScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface ScatterChartProps {
  data: any[];
  xKey: string;
  yKey: string;
  className?: string;
  height?: number;
  color?: string;
  xAxisType?: "number" | "category";
  xAxisLabel?: string;
  yAxisLabel?: string;
  isTimeAxis?: boolean;
  xAxisDomain?: [number, number];
  colorKey?: string; // Key in data object that contains the color value
}

export const ScatterChart: React.FC<ScatterChartProps> = ({
  data,
  xKey,
  yKey,
  className,
  height = 300,
  color = "hsl(var(--primary))",
  xAxisType = "number",
  xAxisLabel,
  yAxisLabel,
  isTimeAxis = false,
  xAxisDomain,
  colorKey,
}) => {
  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsScatterChart
          data={data}
          margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={xKey}
            type={xAxisType}
            name={xAxisLabel || xKey}
            tick={{ fontSize: 12 }}
            tickFormatter={
              isTimeAxis
                ? (value) => format(new Date(value), "MMM dd")
                : undefined
            }
            domain={
              xAxisDomain || (isTimeAxis ? ["dataMin", "dataMax"] : undefined)
            }
          />
          <YAxis
            dataKey={yKey}
            type="number"
            name={yAxisLabel || yKey}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div className="rounded-lg border bg-background p-2 shadow-sm">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="font-medium">{xAxisLabel || xKey}:</div>
                      <div>
                        {isTimeAxis && data.formattedTimestamp
                          ? data.formattedTimestamp
                          : payload[0].payload[xKey]}
                      </div>
                      <div className="font-medium">{yAxisLabel || yKey}:</div>
                      <div>{payload[0].payload[yKey]}</div>
                      {data.cardNumber && (
                        <>
                          <div className="font-medium">Card:</div>
                          <div>{data.cardNumber}</div>
                        </>
                      )}
                      {data.customerName && (
                        <>
                          <div className="font-medium">Customer:</div>
                          <div>{data.customerName}</div>
                        </>
                      )}
                      {data.technicianName && (
                        <>
                          <div className="font-medium">Technician:</div>
                          <div>{data.technicianName}</div>
                        </>
                      )}
                      {data.serviceAdvisorName && (
                        <>
                          <div className="font-medium">Service Advisor:</div>
                          <div>{data.serviceAdvisorName}</div>
                        </>
                      )}
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          {colorKey ? (
            // Multiple colors - create separate scatter plots for each color
            Array.from(
              new Set(data.map((d) => d[colorKey]).filter(Boolean))
            ).map((colorValue, index) => (
              <Scatter
                key={`${colorValue}-${index}`}
                data={data.filter((d) => d[colorKey] === colorValue)}
                dataKey={yKey}
                fill={colorValue as string}
                stroke={colorValue as string}
                strokeWidth={1}
                fillOpacity={0.7}
              />
            ))
          ) : (
            // Single color
            <Scatter
              dataKey={yKey}
              fill={color}
              stroke={color}
              strokeWidth={1}
              fillOpacity={0.7}
            />
          )}
        </RechartsScatterChart>
      </ResponsiveContainer>
    </div>
  );
};
